import React, { useEffect, useState, useContext, useCallback, useMemo, lazy, Suspense } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeContext } from '../context/ThemeContext';
import demosData from '../data/javascripts/demosData';

const ThreadBlocksBestPractices = lazy(() => import('../components/demos/ThreadBlocksBestPractices'));
const CudaWarpShuffle = lazy(() => import('../components/demos/CudaWarpShuffle'));
const CudaThreadBlocksCalculator = lazy(() => import('../components/demos/CudaThreadBlocksCalculator'));
const GpuGenerationsVisualizer = lazy(() => import('../components/demos/GpuGenerationsVisualizer'));

// List the demo slugs for which the header (title and subtitle) should be hidden.
const HIDE_HEADER_SLUGS = [
    'thread-blocks-best-practices'
];

const DemoPage = () => {
    const { darkMode } = useContext(ThemeContext);
    const { demo: demoSlug } = useParams();

    const demo = useMemo(() => demosData.find(d => d.slug === demoSlug), [demoSlug]);
    const [isLoading, setIsLoading] = useState(true);

    // --- Effects & Handlers ---

    useEffect(() => {
        if (demo) {
            window.scrollTo({ top: 0, behavior: 'instant' }); 
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [demo]);

    const renderDemoComponent = useCallback(() => {
        switch (demoSlug) {
            case 'cuda-warp-shuffle':
                return <CudaWarpShuffle />;
            case 'cuda-thread-blocks-calculator':
                return <CudaThreadBlocksCalculator />;
            case 'thread-blocks-best-practices':
                return <ThreadBlocksBestPractices />;
            case 'gpu-generations-visualizer':
                return <GpuGenerationsVisualizer />;
            default:
                return (
                    <div className="text-center py-10">
                        <h3 className="text-xl font-semibold text-red-500">Demo not found</h3>
                        <p className="text-gray-500 dark:text-gray-400">The requested demo doesn't exist yet.</p>
                    </div>
                );
        }
    }, [demoSlug]);

    const themeClasses = useMemo(() => {
        const text = darkMode ? 'text-gray-100' : 'text-gray-900';
        const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
        
        const shareButtonClass = darkMode 
            ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-md' 
            : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-md';

        const baseTagClass = 'px-3 py-1 rounded-full text-xs font-medium';
        const primaryTagClass = 'bg-indigo-500 text-white'; // For Category
        const secondaryTagClass = darkMode 
            ? 'bg-gray-700 text-gray-200 border border-gray-600' // For Tags (Dark)
            : 'bg-gray-200 text-gray-800'; // For Tags (Light)

        return {
            container: `bg-bg-light dark:bg-bg-dark ${text} min-h-screen transition-colors duration-500`,
            textMuted: muted,
            shareButton: shareButtonClass,
            tagCategory: `${baseTagClass} ${primaryTagClass}`,
            tagDefault: `${baseTagClass} ${secondaryTagClass}`,
            spinnerColor: darkMode ? 'border-indigo-400' : 'border-indigo-600'
        };
    }, [darkMode]);
    // --- END FIX ---

    // --- Guards ---
    if (!demo) return <Navigate to="/demos" replace />;

    // Determine if the header should be rendered
    const showHeader = !HIDE_HEADER_SLUGS.includes(demoSlug);

    // --- Render JSX ---
    return (
        <>
            <Helmet>
                <title>{`${demo.title} | Demo by Varun Rao`}</title>
                <meta name="description" content={demo.description} />
                <link rel="canonical" href={window.location.href} />
                <meta property="og:title" content={demo.title} />
                <meta property="og:description" content={demo.description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                {demo.imageUrl && <meta property="og:image" content={window.location.origin + demo.imageUrl} />}
            </Helmet>

            <div className={themeClasses.container}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    {/* Header Block (Conditionally Rendered) */}
                    {showHeader && (
                        <header className="text-center pt-0">
                            <h1 className="text-3xl sm:text-3xl font-extrabold mb-3 text-primary dark:text-secondary">{demo.title}</h1>
                            <p className={`text-md mb-3 ${themeClasses.textMuted}`}>{demo.description}</p>
                        </header>
                    )}

                    {/* Content / Loader */}
                    {isLoading ? (
                        <div className="text-center py-20">
                            <div 
                                className={`
                                    w-12 h-12 inline-block border-4 border-t-transparent rounded-full 
                                    animate-spin ${themeClasses.spinnerColor}
                                `} 
                                role="status"
                            >
                                <span className="sr-only">Loading...</span>
                            </div>
                            <div className="mt-3">Loading interactive demo...</div>
                        </div>
                    ) : (
                        <Suspense
                            fallback={
                                <div className="text-center">
                                    <div 
                                        className={`
                                            w-12 h-12 inline-block border-4 border-t-transparent rounded-full 
                                            animate-spin ${themeClasses.spinnerColor}
                                        `}
                                        role="status"
                                    >
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            }
                        >
                            <div className="demo-content">{renderDemoComponent()}</div>
                        </Suspense>
                    )}
                </div>
            </div>
        </>
    );
};

export default React.memo(DemoPage);